/* eslint-disable complexity */
import React, { FC, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';

import { ECategory } from '@r1-frontend/api-domru/api-profile/v1/spec-offer/update/dto/ResponseDto';

import { Label } from '@r1-frontend/ui-react/components/label';
import WebpImage from '@r1-frontend/ui-react/components/WebpImage';
import { NonStaticContainer } from '@r1-frontend/ui-react/experimental/containers';
import { AdvertisingLabelWithTooltip } from '@r1-frontend/ui-react/organisms/AdvertisingLabelWithTooltip';

import { DEFAULT_PROPS, TAB_INDEX, TEST_NAMES_ATTR, VALUE_IN_ARR } from '~/src/components/PromotionalCard/constants';
import type { IProps } from '~/src/components/PromotionalCard/types';
import type { TState } from '~/src/store';
import { selectPromoCategory } from '~/src/store/selectors/promo';

import * as ST from './styled';

const NEW_LABEL_TITLE = 'новое';

/**
 * Компонент карточка акций
 *
 * Компонент логически настроен на один из трех сценариев - либо callback, либо link, либо popup (renderPopup, popupProps)
 * Размер контейнера компонента рассчитан на один длинный текст в label блоке - promoText или duration
 *
 * @function
 * @name components.PromotionalCard
 * @returns {JSX.Element}
 */
export const PromotionalCard:FC<IProps> = (props) => {
    const pureProps = Object.fromEntries(Object.entries(props).filter((elem) => elem[VALUE_IN_ARR] !== undefined));
    const realProps = { ...DEFAULT_PROPS, ...pureProps };
    const {
        id,
        labelData,
        title,
        description,
        callback,
        btnTypes,
        symbolId,
        iconsSprite,
        renderPopup,
        popupProps,
        link,
        textConnected,
        textNotConnected,
        isConnected,
        duration,
        promoText,
        setIsPopupOpenOut,
        cardKey,
        setCardKey,
        sendVasGA,
        imgUrl,
        imgWebp,
        advertiserName,
        advertiserErid,
    } = realProps;
    const { buttonAttr, descriptionAttr, titleAttr, image, label, container, labelsBlock, textLabelAttr, promoTextAttr } = TEST_NAMES_ATTR;
    const [isPopupOpen, setIsPopupOpenInner] = useState(false);
    const selectCategory = useSelector((state: TState) => (id: number) => selectPromoCategory(state, id));
    /** Необходимо для slider контейнера */
    const setIsPopupOpen = setIsPopupOpenOut || setIsPopupOpenInner;

    const renderPopupMemoized = useMemo(() => {
        const renderPopupTrueTyped = renderPopup as NonNullable<IProps['renderPopup']>;
        const popupPropsTrueTyped = popupProps as NonNullable<IProps['popupProps']>;
        return () => renderPopupTrueTyped({ ...popupPropsTrueTyped, isPopupOpen, setIsPopupOpen });
    }, [isPopupOpen, popupProps, renderPopup]);

    const pureDescription = useMemo(
        () => description?.replace(/<[^>]+>/g, ''),
        [description],
    );

    const callbackWrap = () => {
        if (callback) {
            callback();
            return;
        }
        /** Необходимо для slider контейнера */
        setCardKey && setCardKey(cardKey);
        setIsPopupOpen(true);
    };

    const callbackLink = () => {
        sendVasGA && sendVasGA('click details btn', title);
    };

    const buttonIconProps = {
        iconsSprite,
        symbolId,
    };

    /** @TODO Удалить и сделать простое условие, когда всем карточкам добавят textButton в api и не будет хардкода */
    const getButtonText = () => {
        const text = isConnected ? textConnected : textNotConnected;
        const textDefault = isConnected ? DEFAULT_PROPS.textConnected : DEFAULT_PROPS.textNotConnected;
        return text || textDefault;
    };

    const linkData = {
        href: link,
        target: '_blank',
        rel: 'noopener noreferrer',
    };

    const renderLabel = (text: string | undefined, id: number, color: string, textColor: string) => {
        if (!text) {
            return null;
        }

        return (
            <Label
                key={id}
                textColor={textColor}
                bgColor={color}
                data-test={`${label}-${text}`}
            >
                {text}
            </Label>
        );
    };

    const checkRenderLabelBlock = () => Boolean(labelData.length || duration || promoText);

    return (
        <ThemeProvider theme={realProps}>
            <ST.WrapperPromotionalCard data-test={container} tabIndex={TAB_INDEX}>
                <ST.ImageWrap>
                    <WebpImage urlWebp={imgWebp}>
                        <ST.HeaderImage data-test={image} src={imgUrl} alt={`изображение акции ${title || ''}`}/>
                    </WebpImage>
                    {advertiserName && advertiserErid &&
                        <NonStaticContainer $united={{ $bottom: 12, $right: 12 }}>
                            <AdvertisingLabelWithTooltip
                                companyName={advertiserName}
                                erid={advertiserErid}
                                tooltip={{
                                    position: 'top-right',
                                }}
                            />
                        </NonStaticContainer>
                    }
                </ST.ImageWrap>
                <ST.Container>
                    <ST.BodyCard>
                        {checkRenderLabelBlock() &&
                            <ST.LabelsBlock data-test={labelsBlock}>
                                {/* Переделать, когда хардкод с лейблом новое перенесут на BE */}
                                {!!labelData.length && labelData.map(({ text, color, textColor }, index) => (
                                    text === NEW_LABEL_TITLE
                                        ? id && selectCategory(id) === ECategory.new && renderLabel(text, index, color, textColor)
                                        : renderLabel(text, index, color, textColor)
                                ))}
                                {(duration) && <ST.TextLabel data-test={textLabelAttr} dangerouslySetInnerHTML={{ __html: duration }}/>}
                                {(promoText && !duration) && <ST.TextLabel data-test={promoTextAttr} >{promoText}</ST.TextLabel>}
                            </ST.LabelsBlock>
                        }
                        {title &&
                            <ST.Title
                                data-test={titleAttr}
                                dangerouslySetInnerHTML={{ __html: title }}
                            />
                        }
                        {description &&
                            <ST.Description
                                data-test={descriptionAttr}
                                dangerouslySetInnerHTML={{ __html: pureDescription }}
                            />
                        }
                    </ST.BodyCard>
                    <ST.ButtonBlock>
                        <ST.Button
                            data-test={buttonAttr}
                            type={btnTypes}
                            text={getButtonText()}
                            { ...(isConnected ? buttonIconProps : '') }
                            { ...(link ? { ...linkData, onClick: callbackLink } : { onClick: callbackWrap }) }
                        />
                    </ST.ButtonBlock>
                </ST.Container>
            </ST.WrapperPromotionalCard>
            {renderPopup && popupProps && renderPopupMemoized()}
        </ThemeProvider>
    );
};

export const MemoizedPromotionalCard = React.memo(PromotionalCard);
